// Lot 2b — Orchestration des cibles de force : quand les créer, quand les ajuster,
// et quoi afficher sur le radar. Le calcul lui-même vit dans `strengthStandards.ts`
// (module pur) ; ici on ne fait que lire l'état du client et déclencher ce calcul.
//
// ⚠️ VOCABULAIRE : le pointillé du radar s'appelle « Cible », jamais du nom d'un niveau.
// Voir l'avertissement en tête de `strengthStandards.ts`.

import {
  supabase,
  getAthleteProfile,
  getCurrentBodyweightKg,
  getProgramTestingSessions,
  getStrengthBlockTargets,
  insertStrengthBlockTargets,
  applyMidBlockAdjustment,
  markMidBlockChecked,
  type WizardSubmissionRow,
  type StrengthBlockTargetRow,
  type StrengthBlockTargetInput,
} from "@/lib/supabase"
import { getProgrammeIdFromWizard } from "@/lib/programs"
import { toLocalISODate } from "@/lib/utils"
import {
  STRENGTH_MOVEMENTS,
  RM_FIELD_BY_MOVEMENT,
  computeBlockTarget,
  adjustTargetAtMidBlock,
  comparableValue,
  resolveAgeBracket,
  toStandardsGender,
  bodyweightBandLabel,
  standardForBodyweight,
  type StrengthMovement,
  type StandardsGender,
  type AgeBracket,
} from "@/lib/strengthStandards"

// ── Phase du bloc ───────────────────────────────────────────

/**
 * Où en est le client dans les trois séances de test de son bloc.
 * `none` = le test initial n'a pas encore été passé, donc aucune cible n'existe.
 */
export type BlockTestPhase = "none" | "initial" | "mid" | "final"

export interface BlockContext {
  programmeId: string
  blockStartDate: string
  testingSessions: number[]
  maxCompletedSession: number
  phase: BlockTestPhase
}

/**
 * Numéro de séance le plus avancé réellement complété sur ce programme.
 * Même source de vérité que `getNextSessionNumber` (`session_feedback` avec un RPE
 * non nul = séance complète, un RPE nul étant un enregistrement partiel).
 */
async function getMaxCompletedSession(userId: string, programmeId: string): Promise<number> {
  const { data, error } = await supabase
    .from("session_feedback")
    .select("programme_session_number")
    .eq("user_id", userId)
    .eq("programme_id", programmeId)
    .not("programme_session_number", "is", null)
    .not("rpe", "is", null)
  if (error || !data || data.length === 0) return 0
  return data.reduce((max, r) => {
    const n = r.programme_session_number as number
    return Number.isFinite(n) && n > max ? n : max
  }, 0)
}

/**
 * Une séance de test compte comme passée dès qu'une séance de rang supérieur ou égal est
 * complétée : le calendrier verrouille les séances séquentiellement (cf. S17), donc un
 * numéro plus avancé implique d'être passé par le test. Comparer au seul numéro exact
 * ferait disparaître la cible d'un client qui a sauté l'enregistrement de cette séance-là.
 */
function resolvePhase(testingSessions: number[], maxCompleted: number): BlockTestPhase {
  const [initial, mid, final] = testingSessions
  if (final != null && maxCompleted >= final) return "final"
  if (mid != null && maxCompleted >= mid) return "mid"
  if (initial != null && maxCompleted >= initial) return "initial"
  return "none"
}

export async function getBlockContext(wizard: WizardSubmissionRow): Promise<BlockContext | null> {
  const programmeId = getProgrammeIdFromWizard(wizard)
  const blockStartDate = wizard.program_week_start
  if (!blockStartDate) return null

  const [testingSessions, maxCompletedSession] = await Promise.all([
    getProgramTestingSessions(programmeId),
    getMaxCompletedSession(wizard.user_id, programmeId),
  ])

  return {
    programmeId,
    blockStartDate,
    testingSessions,
    maxCompletedSession,
    phase: resolvePhase(testingSessions, maxCompletedSession),
  }
}

// ── Données du radar ────────────────────────────────────────

export interface StrengthAxisEntry {
  movement: StrengthMovement
  /** Valeur brute d'`athlete_profiles` : kg à la barre, charge TOTALE en kg pour la traction. */
  rawRmValue: number | null
  /** Valeur comparable aux tables : kg à la barre, ratio de charge totale pour la traction. */
  currentValue: number | null
  /** Cible du bloc, dans la même unité que `currentValue`. Null tant qu'aucun test initial. */
  targetValue: number | null
  /** currentValue / targetValue, non plafonné (un dépassement se voit). */
  ratioOfTarget: number | null
  noviceStandard: number | null
  intermediateStandard: number | null
  /** true quand la valeur du test final dépasse la cible du bloc. */
  exceededTarget: boolean
  /**
   * Valeur du test initial du bloc, dans la même unité que `currentValue`. Sert à
   * l'historique d'un record (lot 1) : c'est le seul point de départ daté dont on dispose,
   * `athlete_profiles` ne gardant que le dernier 1RM connu.
   */
  initialValue: number | null
  /** Date de création de la cible = date du test initial retenu pour ce bloc. */
  initialTestDate: string | null
  /**
   * Poids de corps figé au test initial. Indispensable pour reconvertir `initialValue`
   * en kg sur la traction lestée : le ratio a été calculé avec CE poids-là, pas avec
   * le poids d'aujourd'hui, et utiliser le mauvais fabriquerait un écart qui n'existe pas.
   */
  initialBodyweightKg: number | null
  /** Date du constat de mi-bloc, quand il a eu lieu. */
  midBlockMeasuredAt: string | null
}

export interface StrengthRadarData {
  gender: StandardsGender
  bracket: AgeBracket
  /** true quand l'âge du client sort des tranches couvertes et qu'on a rabattu sur une borne. */
  bracketClamped: boolean
  bodyweightKg: number
  /** Tranche de poids des tables, ex. « 67–75 ». Informatif : le calcul interpole au poids exact. */
  bodyweightBand: string | null
  phase: BlockTestPhase
  /** Début du bloc en cours (wizard_submissions.program_week_start au test initial). */
  blockStartDate: string | null
  entries: StrengthAxisEntry[]
  /** Mouvements dont le test final dépasse la cible — déclenchent le message de félicitations. */
  exceeded: StrengthMovement[]
  /** true si au moins une cible est active sur le bloc en cours. */
  hasTargets: boolean
}

function rmValueFor(
  profile: Record<string, unknown> | null,
  movement: StrengthMovement
): number | null {
  if (!profile) return null
  const v = profile[RM_FIELD_BY_MOVEMENT[movement]]
  return typeof v === "number" && Number.isFinite(v) ? v : null
}

/**
 * Charge tout ce qu'il faut pour peindre le radar, en créant ou ajustant les cibles du bloc
 * au passage si une séance de test vient d'être franchie.
 *
 * Le déclenchement est PARESSEUX (à la lecture) plutôt que branché sur la fin de séance :
 * les valeurs de test ne sont pas capturées pendant la séance, elles arrivent par la
 * saisie manuelle des 1RM sur le Profil. Attacher la création de cible à la complétion de
 * la séance figerait la cible sur un 1RM pas encore mis à jour. Les écritures restent
 * idempotentes (upsert `ignoreDuplicates` + marqueur `mid_block_measured_at`), donc appeler
 * cette fonction à chaque ouverture du Profil ne recrée ni ne fait dériver quoi que ce soit.
 */
export async function loadStrengthRadarData(
  wizard: WizardSubmissionRow
): Promise<StrengthRadarData | null> {
  const [athleteProfile, bodyweightFromHistory, block] = await Promise.all([
    getAthleteProfile(),
    getCurrentBodyweightKg(wizard.user_id),
    getBlockContext(wizard),
  ])

  const bodyweightKg = bodyweightFromHistory ?? wizard.weight
  if (!bodyweightKg || bodyweightKg <= 0) return null

  const gender = toStandardsGender(wizard.gender)
  const { bracket, clamped } = resolveAgeBracket(wizard.birth_date, wizard.age)
  const profileRecord = athleteProfile as unknown as Record<string, unknown> | null

  let targets: StrengthBlockTargetRow[] = []

  if (block && block.phase !== "none") {
    targets = await getStrengthBlockTargets(block.programmeId, block.blockStartDate)

    // Une cible créée dans CETTE passe l'est forcément à partir du 1RM courant. Si le
    // client est déjà au-delà de la séance de test intermédiaire (cas d'un bloc entamé
    // avant la mise en service du radar), cette création est rétroactive : le « test
    // initial » et la « mesure intermédiaire » seraient alors la même lecture.
    const createdNow = targets.length === 0

    if (createdNow) {
      targets = await createBlockTargets({
        block,
        gender,
        bracket,
        bodyweightKg,
        profileRecord,
        beginnerExperience:
          wizard.level_id === "beginner" ? wizard.beginner_experience ?? null : null,
      })
    }

    if ((block.phase === "mid" || block.phase === "final") && targets.length > 0) {
      if (createdNow) {
        // ⚠️ Ne JAMAIS ajuster une cible qu'on vient de créer. Le déclencheur
        // `mesure >= 0,90 × cible` avec `cible = mesure × (1 + pct)` se réduit à
        // `1 >= 0,90 × (1 + pct)`, vrai pour tout pct <= 11,1 % : les zones à 8/6/4/3 %
        // se rehausseraient automatiquement dès la première ouverture du Profil, sans
        // qu'aucun test intermédiaire n'ait eu lieu (les zones à 15/20 %, elles, ne
        // déclenchaient pas — d'où un bug qui ne touchait qu'une partie des mouvements).
        // On fige donc le constat sans toucher à la cible : la valeur de référence a été
        // relevée en cours de bloc, il n'y a pas de progression à évaluer entre les deux.
        targets = await markBlockTargetsMidChecked(targets)
      } else {
        targets = await runMidBlockAdjustment(targets, profileRecord, bodyweightKg)
      }
    }
  }

  const targetByMovement = new Map(targets.map((t) => [t.movement, t]))

  const entries: StrengthAxisEntry[] = STRENGTH_MOVEMENTS.map((movement) => {
    const rawRmValue = rmValueFor(profileRecord, movement)
    const currentValue = comparableValue(movement, rawRmValue, bodyweightKg)
    const target = targetByMovement.get(movement) ?? null
    const targetValue = target?.target_value ?? null

    return {
      movement,
      rawRmValue,
      currentValue,
      targetValue,
      ratioOfTarget:
        currentValue != null && targetValue != null && targetValue > 0
          ? currentValue / targetValue
          : null,
      noviceStandard: standardForBodyweight(movement, gender, bracket, bodyweightKg, "novice"),
      intermediateStandard: standardForBodyweight(movement, gender, bracket, bodyweightKg, "intermediate"),
      exceededTarget:
        block?.phase === "final" &&
        currentValue != null &&
        targetValue != null &&
        currentValue > targetValue,
      initialValue: target?.initial_value ?? null,
      initialTestDate: target?.created_at ?? null,
      initialBodyweightKg: target?.initial_bodyweight_kg ?? null,
      midBlockMeasuredAt: target?.mid_block_measured_at ?? null,
    }
  })

  return {
    gender,
    bracket,
    bracketClamped: clamped,
    bodyweightKg,
    bodyweightBand: bodyweightBandLabel(gender, bracket, bodyweightKg),
    phase: block?.phase ?? "none",
    blockStartDate: block?.blockStartDate ?? null,
    entries,
    exceeded: entries.filter((e) => e.exceededTarget).map((e) => e.movement),
    hasTargets: targets.length > 0,
  }
}

/** Cibles du test initial : une ligne par mouvement dont le 1RM est renseigné. */
async function createBlockTargets(params: {
  block: BlockContext
  gender: StandardsGender
  bracket: AgeBracket
  bodyweightKg: number
  profileRecord: Record<string, unknown> | null
  beginnerExperience: WizardSubmissionRow["beginner_experience"]
}): Promise<StrengthBlockTargetRow[]> {
  const { block, gender, bracket, bodyweightKg, profileRecord, beginnerExperience } = params

  const inputs: StrengthBlockTargetInput[] = []
  for (const movement of STRENGTH_MOVEMENTS) {
    const computed = computeBlockTarget({
      movement,
      gender,
      bracket,
      bodyweightKg,
      rawRmValue: rmValueFor(profileRecord, movement),
      beginnerExperience,
    })
    if (!computed) continue
    inputs.push({
      programme_id: block.programmeId,
      block_start_date: block.blockStartDate,
      movement,
      initial_value: computed.initialValue,
      initial_bodyweight_kg: bodyweightKg,
      initial_percent: computed.percent,
      zone: computed.zone,
      target_value: computed.targetValue,
    })
  }

  if (inputs.length === 0) return []

  try {
    const inserted = await insertStrengthBlockTargets(inputs)
    // `ignoreDuplicates` renvoie une liste vide si les lignes existaient déjà (deux onglets
    // ouverts en même temps) : on relit plutôt que de rendre un radar sans cible.
    if (inserted.length > 0) return inserted
    return await getStrengthBlockTargets(block.programmeId, block.blockStartDate)
  } catch (err) {
    console.error("[strengthTargets] création des cibles échouée :", err)
    return []
  }
}

/**
 * Marque des cibles comme « intermédiaire déjà constaté » sans rien rehausser.
 *
 * Utilisé quand les cibles viennent d'être créées rétroactivement alors que le client a
 * déjà dépassé la séance de test intermédiaire : la valeur de référence et la mesure
 * étant la même lecture, il n'y a pas de progression à évaluer. Le marqueur est persisté
 * pour que la passe suivante (qui, elle, trouvera des cibles existantes) ne rejoue pas
 * l'ajustement sur cette même base.
 */
async function markBlockTargetsMidChecked(
  targets: StrengthBlockTargetRow[]
): Promise<StrengthBlockTargetRow[]> {
  const today = toLocalISODate(new Date())
  const updated: StrengthBlockTargetRow[] = []
  for (const target of targets) {
    if (target.mid_block_adjusted || target.mid_block_measured_at) {
      updated.push(target)
      continue
    }
    await markMidBlockChecked(target.id, today)
    updated.push({ ...target, mid_block_measured_at: today })
  }
  return updated
}

/**
 * Ajustement au test intermédiaire, mouvement par mouvement.
 *
 * ⚠️ `adjustTargetAtMidBlock` repart de `initial_value` (test initial) et jamais de la
 * mesure intermédiaire — sans quoi la cible dériverait à chaque recalcul. `initial_percent`
 * n'est pas réécrit non plus, pour que le résultat soit le même quel que soit le nombre de
 * fois où cette fonction tourne.
 */
async function runMidBlockAdjustment(
  targets: StrengthBlockTargetRow[],
  profileRecord: Record<string, unknown> | null,
  bodyweightKg: number
): Promise<StrengthBlockTargetRow[]> {
  const today = toLocalISODate(new Date())
  const updated: StrengthBlockTargetRow[] = []

  for (const target of targets) {
    // Une cible déjà rehaussée, ou déjà constatée sous le seuil, ne se réévalue plus :
    // le 1RM continue de bouger après le test, la décision doit rester celle du test.
    if (target.mid_block_adjusted || target.mid_block_measured_at) {
      updated.push(target)
      continue
    }

    const measured = comparableValue(
      target.movement,
      rmValueFor(profileRecord, target.movement),
      bodyweightKg
    )
    if (measured == null) {
      updated.push(target)
      continue
    }

    const result = adjustTargetAtMidBlock({
      initialValue: target.initial_value,
      initialPercent: target.initial_percent,
      currentTarget: target.target_value,
      measuredValue: measured,
    })

    if (!result.triggered) {
      await markMidBlockChecked(target.id, today)
      updated.push({ ...target, mid_block_measured_at: today })
      continue
    }

    const row = await applyMidBlockAdjustment(target.id, result.targetValue, today)
    updated.push(row ?? target)
  }

  return updated
}
